import { formControl, Validators } from '@al00x/forms';
import { Component, inject, signal } from '@angular/core';
import { CacBaseDialogComponent, CacDialogLayoutComponent, CacFormBuilderComponent, CacFieldComponent, formBuilder, ButtonClickEvent } from '@controladad/ng-base';
import { PermissionBox, PermissionBoxComponent } from './permission-box/permission-box.component';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionEntity, RoleCreate, RoleDetails } from '@admin/core/models';
import { RoleApiService } from '@admin/core/services/role-api.service';
import { AuthStore } from '@admin/core';

export interface RoleDialogData {
  role?: RoleDetails;
  permissions: PermissionEntity[];
};

export type RoleDialogResult = boolean;

interface PermissionGroup {
  label: string;
  permissions: PermissionBox[];
}

@Component({
  selector: 'app-role-dialog',
  imports: [CacDialogLayoutComponent, CacFormBuilderComponent, CacFieldComponent, PermissionBoxComponent],
  templateUrl: './role-dialog.component.html',
})
export class RoleDialogComponent extends CacBaseDialogComponent<
  RoleDialogData,
  RoleDialogResult
> {
  private readonly roleApi = inject(RoleApiService);
  private readonly auth = inject(AuthStore);

  baseForm = formBuilder({
    cols: 1,
    inputs: {
      name: {
        control: formControl('', Validators.required),
        label: 'نام نقش',
      },
    },
  });
  searchControl = formControl('');

  groups = signal<PermissionGroup[]>([]);
  filteredGroups = signal<PermissionGroup[]>([]);

  constructor() {
    super();
    this.convertToGroup(this.data.permissions);
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(searchTerm => {
        this.filterGroups(searchTerm || '');
      });

    const role = this.data.role;
    if (!role) return;
    this.baseForm.setValue({
      name: role.name,
    });
  }

  private convertToGroup(permissions: PermissionEntity[]) {
    const selectedPermissions = new Set(this.data.role?.permissions);
    const panelMap = new Map<string, PermissionGroup>();
    permissions.forEach(permission => {
      let group = panelMap.get(permission.panel);
      if (!group) {
        group = {
          label: permission.panel,
          permissions: []
        };
        panelMap.set(permission.panel, group);
      }
      const permissionBox: PermissionBox = {
        label: permission.module,
        hasSelectAll: true,
        items: permission.permissions.map(item => ({
          label: item.label,
          key: item.key,
          id: item.id,
          control: formControl(selectedPermissions.has(item.id)),
        }))
      };
      group.permissions.push(permissionBox);
    });
    const groups = Array.from(panelMap.values());
    this.groups.set(groups);
    this.filteredGroups.set(groups);
  }

  private filterGroups(searchTerm: string) {
    const groups = this.groups()
    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredGroups.set(groups);
      return;
    }
    const term = searchTerm.toLowerCase().trim();
    const filtered: PermissionGroup[] = groups
      .map(group => ({
        ...group,
        permissions: group.permissions
          .map(module => ({
            ...module,
            items: module.items.filter(item => item.label.toLowerCase().includes(term))
          }))
          .filter(module => module.items.length > 0)
      }))
      .filter(group => group.permissions.length > 0);
    this.filteredGroups.set(filtered);
  }

  private getSelectedPermissions(): number[] {
    const groups = this.groups();
    const selected: number[] = [];
    groups.forEach(group => {
      group.permissions.forEach(box => {
        box.items.forEach(item => {
          if (item.control.value === true) {
            selected.push(item.id);
          }
        });
      });
    });
    return selected;
  }

  onSubmit(e: ButtonClickEvent) {
    const baseModel = this.baseForm.getValue();
    if (!baseModel) return;
    const selectedPermissions = this.getSelectedPermissions();
    if (!selectedPermissions.length) {
      snackbar$.error('حداقل یک دسترسی باید انتخاب شود.');
      return;
    }
    const model: RoleCreate = {
      name: baseModel.name,
      permissionIds: selectedPermissions,
    };
    const roleId = this.data.role?.id;
    (roleId ? this.roleApi.updateRole(roleId, model) : this.roleApi.createRole(model)).pipe(
      switchMap(() => this.auth.updateProfile()),
      e.pipe()
    ).subscribe(() => {
      this.submit(true);
    });
  }
}
